import { useMemo } from 'react';
import { OptionKey, SpendPeriodType } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT, MOUNTH_YEAR_FORMAT } from '@/common/constants';
import { ISpendLimitPeriodDto } from '@/dtos';

import { useGetSpendLimitPeriodsQuery } from '../queries';

const AVAIBLE_NUMBER_OF_MONTHS = 12;
const formatPeriods = (data: ISpendLimitPeriodDto) => {
  const planYears = data?.plan_years?.map((year) => ({
    id: _.toString(year.id),
    name: year.name,
    validFrom: year.valid_from,
    validTo: year.valid_to || undefined,
  }));

  planYears?.sort((a, b) => {
    if (!a.validTo) return -1;
    if (!b.validTo) return 1;
    return new Date(b?.validTo).getTime() - new Date(a.validTo).getTime();
  });

  return {
    periodStart: data.period_start,
    periodEnd: data.period_end,
    spendPeriodType: data.spend_period_type as unknown as SpendPeriodType,
    planYears: planYears || [],
  };
};

const useGetSpendLimitPeriod = (id?: string) => {
  const { data, isLoading } = useGetSpendLimitPeriodsQuery(id);

  const formattedData = useMemo(() => {
    return data ? formatPeriods(data) : undefined;
  }, [data]);
  const planYearOptions = useMemo(() => {
    if (!formattedData) return [];
    return formattedData.planYears.map((year) => ({
      key: year.validFrom,
      value: year.validFrom,
      title: year.name,
    }));
  }, [formattedData]);

  const monthOptions = useMemo(() => {
    if (!formattedData) return [];

    const periodStart = dayjs(formattedData.periodStart);
    const periodEnd = formattedData.periodEnd ? dayjs(formattedData.periodEnd) : undefined;

    // For case when period_start in the future
    if (periodStart.isAfter(dayjs())) {
      let months: dayjs.Dayjs[] = [];
      if (!periodEnd) {
        // 1) select 12 months maximum from period_start
        months = Array.from({ length: AVAIBLE_NUMBER_OF_MONTHS }, (_, i) => periodStart.add(i, 'month'));
      } else {
        // 2) select less than 12 months from period_start to period_end
        const totalMonths = periodEnd.diff(periodStart, 'month') + 1;
        const count = Math.min(totalMonths, AVAIBLE_NUMBER_OF_MONTHS);
        months = Array.from({ length: count }, (_, i) => periodStart.add(i, 'month'));
      }
      return months.map((month) => ({
        key: month.startOf('month').format(DEFAULT_DATE_FORMAT),
        value: month.startOf('month').format(DEFAULT_DATE_FORMAT),
        title: month.format(MOUNTH_YEAR_FORMAT),
      }));
    }

    const currentMonth = dayjs();
    const startMonth = periodEnd && periodEnd.isBefore(currentMonth) ? periodEnd : currentMonth;
    const months = Array.from({ length: AVAIBLE_NUMBER_OF_MONTHS }, (_, i) => startMonth.subtract(i, 'month'))
      .filter((month) => !month.isBefore(periodStart))
      .map((month) => ({
        name: month.format(MOUNTH_YEAR_FORMAT),
        firstDate: month.startOf('month').format(DEFAULT_DATE_FORMAT),
      }));

    return months.map((month) => ({
      key: month.firstDate,
      value: month.firstDate,
      title: month.name,
    }));
  }, [formattedData]);

  const defaultPeriod: OptionKey = useMemo(() => {
    if (!formattedData) return '';

    if (formattedData.spendPeriodType === SpendPeriodType.PLAN_YEAR) {
      const now = dayjs();
      const currentYearPlan = formattedData.planYears?.find((year) => {
        const from = dayjs(year.validFrom);
        const to = year.validTo ? dayjs(year.validTo) : undefined;
        return (
          (now.isSame(from, 'day') || now.isAfter(from, 'day')) &&
          (!to || now.isSame(to, 'day') || now.isBefore(to, 'day'))
        );
      });
      return (currentYearPlan?.validFrom || formattedData.planYears?.[0]?.validFrom || '') as string;
    }

    if (formattedData.spendPeriodType === SpendPeriodType.MONTHLY) {
      return (monthOptions?.[0]?.value || '') as string;
    }

    if (formattedData.spendPeriodType === SpendPeriodType.UNLIMITED) {
      return dayjs().format(DEFAULT_DATE_FORMAT);
    }

    return undefined;
  }, [formattedData, monthOptions]);

  return {
    planYearOptions,
    monthOptions,
    defaultPeriod,
    formattedData,
    isLoading,
  };
};

export default useGetSpendLimitPeriod;
